import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaBriefcaseMedical, FaCalendarAlt, FaUserInjured, FaPrescriptionBottle,
  FaFlask, FaCreditCard, FaVideo, FaFileMedical, FaPills,
  FaChartBar, FaCog, FaQuestionCircle, FaTimes, FaCommentDots,
  FaShieldAlt, FaBell, FaSignOutAlt, FaUserMd
} from 'react-icons/fa';
import { useApp, useRolePath } from '../context/AppContext';
import { getFileUrl } from '../services/api';

const Sidebar = () => {
  const { sidebarOpen, closeSidebar, user, logout, badges } = useApp();
  const navigate  = useNavigate();
  const location  = useLocation();
  const rp        = useRolePath();

  const role = user?.role || 'Doctor';

  const handleNav = (path) => { navigate(path); closeSidebar(); };
  const handleLogout = () => {
    const r = user?.role?.toLowerCase() || 'patient';
    logout();
    navigate(`/${r}/login`);
    closeSidebar();
  };

  const mainMenu = [
    { label: 'Appointments', icon: FaCalendarAlt,        path: rp('/appointments'), badge: badges.appointments },
    { label: 'Specialists', icon: FaUserMd, path: rp('/specialists') },
    ...(role !== 'Patient' ? [{ label: 'Patients', icon: FaUserInjured, path: rp('/patients') }] : []),
    { label: 'Prescriptions', icon: FaPrescriptionBottle, path: rp('/prescriptions'), badge: badges.prescriptions },
    { label: 'Lab Tests',     icon: FaFlask,              path: rp('/labtests'),      badge: badges.labTests },
    { label: 'Billing',       icon: FaCreditCard,          path: rp('/billing'),       badge: badges.billing },
    { label: 'Telemedicine',  icon: FaVideo,               path: rp('/telemedicine'),  badge: badges.telemedicine },
    { label: 'EMR',      icon: FaFileMedical, path: rp('/emr') },
    { label: 'Pharmacy', icon: FaPills,       path: rp('/pharmacy') },
    { label: 'Messages', icon: FaCommentDots, path: rp('/messages'), badge: badges.messages },
  ];

  const moreMenu = [
    ...(role !== 'Patient' ? [
      { label: 'Calendar',           icon: FaCalendarAlt, path: rp('/calendar') },
      { label: 'Reports & Analytics', icon: FaChartBar,   path: rp('/reports') },
    ] : []),
    { label: 'Notifications', icon: FaBell,         path: rp('/notifications'), badge: badges.notifications },
    { label: 'Settings',      icon: FaCog,          path: rp('/settings') },
    { label: 'Help & Support', icon: FaQuestionCircle, path: rp('/help') },
    ...(role === 'Admin' ? [{ label: 'Admin Panel', icon: FaShieldAlt, path: rp('/panel') }] : []),
  ];

  const roleColor = role === 'Admin' ? '#7c3aed' : role === 'Doctor' ? '#1976d2' : '#2e7d32';
  const initials  = user ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : 'DR';

  const MenuItem = ({ item }) => (
    <div
      className={`sidebar-item ${location.pathname === item.path ? 'active' : ''}`}
      onClick={() => handleNav(item.path)}
    >
      <div className="sidebar-item-icon"><item.icon /></div>
      <span className="sidebar-item-label">{item.label}</span>
      {item.badge > 0 && <span className="sidebar-badge">{item.badge > 99 ? '99+' : item.badge}</span>}
    </div>
  );

  return (
    <>
      <style>{`
        .sidebar-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.55);
          z-index: 800; animation: fadeIn 0.2s ease;
        }
        @media (min-width: 768px) { .sidebar-overlay { display: none !important; } }
        .sidebar {
          position: fixed; top: 0; left: 0; bottom: 0;
          width: var(--sidebar-width);
          background: white; z-index: 900;
          display: flex; flex-direction: column;
          box-shadow: 4px 0 20px rgba(0,0,0,0.08);
          transform: translateX(-100%);
          transition: transform 0.28s cubic-bezier(0.4,0,0.2,1);
        }
        .sidebar.open { transform: translateX(0); }
        @media (min-width: 768px) {
          .sidebar { transform: translateX(0) !important; box-shadow: 2px 0 12px rgba(0,0,0,0.06); border-right: 1px solid var(--border); }
          .sidebar-close { display: none !important; }
        }
        .sidebar-header { background: linear-gradient(135deg,#E8500A 0%,#FF6B2C 100%); padding: 16px; display: flex; align-items: center; justify-content: space-between; flex-shrink: 0; }
        .sidebar-header-left { display: flex; align-items: center; gap: 10px; }
        .sidebar-logo { background: rgba(255,255,255,0.22); width: 42px; height: 42px; border-radius: 10px; display: flex; align-items: center; justify-content: center; color: white; font-size: 20px; }
        .sidebar-title { color: white; font-size: 13px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; line-height: 1.3; }
        .sidebar-subtitle { color: rgba(255,255,255,0.75); font-size: 11px; }
        .sidebar-close { background: rgba(255,255,255,0.18); border: none; width: 34px; height: 34px; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; cursor: pointer; flex-shrink: 0; }
        .sidebar-close:hover { background: rgba(255,255,255,0.28); }
        .sidebar-user { padding: 14px 16px; border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 12px; }
        .sidebar-user-avatar { width: 44px; height: 44px; border-radius: 50%; background: linear-gradient(135deg,#E8500A,#FF6B2C); display: flex; align-items: center; justify-content: center; color: white; font-size: 16px; font-weight: 700; flex-shrink: 0; }
        .sidebar-user-name { font-size: 14px; font-weight: 700; color: var(--text-dark); }
        .sidebar-user-role { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 10px; display: inline-block; margin-top: 2px; }
        .sidebar-nav { flex: 1; overflow-y: auto; padding: 8px 0; }
        .sidebar-nav-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--text-light); padding: 8px 20px 4px; }
        .sidebar-item { display: flex; align-items: center; gap: 14px; padding: 11px 16px; cursor: pointer; transition: all 0.18s ease; border-left: 3px solid transparent; margin: 1px 8px; border-radius: 10px; }
        .sidebar-item:hover { background: var(--primary-bg); border-left-color: var(--primary); }
        .sidebar-item.active { background: var(--primary-bg); border-left-color: var(--primary); }
        .sidebar-item.active .sidebar-item-label { color: var(--primary); font-weight: 700; }
        .sidebar-item-icon { width: 34px; height: 34px; background: var(--primary-bg); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: var(--primary); font-size: 15px; flex-shrink: 0; transition: background 0.18s; }
        .sidebar-item.active .sidebar-item-icon, .sidebar-item:hover .sidebar-item-icon { background: var(--primary); color: white; }
        .sidebar-item-label { font-size: 14px; font-weight: 500; color: var(--text-medium); flex: 1; }
        .sidebar-badge { background: var(--primary); color: white; font-size: 11px; font-weight: 700; min-width: 20px; height: 20px; border-radius: 10px; display: flex; align-items: center; justify-content: center; padding: 0 4px; }
        .sidebar-divider { height: 1px; background: var(--border); margin: 6px 16px; }
        .sidebar-footer { padding: 12px 16px; border-top: 1px solid var(--border); flex-shrink: 0; }
        .sidebar-logout { display: flex; align-items: center; gap: 12px; padding: 10px 12px; cursor: pointer; border-radius: 10px; color: #c62828; transition: background 0.18s; }
        .sidebar-logout:hover { background: #ffebee; }
        .sidebar-logout-icon { width: 34px; height: 34px; background: #ffebee; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 15px; }
        .sidebar-logout-label { font-size: 14px; font-weight: 600; }
      `}</style>

      {sidebarOpen && <div className="sidebar-overlay" onClick={closeSidebar} />}

      <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-header-left">
            <div className="sidebar-logo"><FaBriefcaseMedical /></div>
            <div>
              <div className="sidebar-title">DocApps Solutions</div>
              <div className="sidebar-subtitle">Pvt Ltd</div>
            </div>
          </div>
          <button className="sidebar-close" onClick={closeSidebar}><FaTimes /></button>
        </div>

        {user && (
          <div className="sidebar-user" style={{ cursor: 'pointer' }} onClick={() => handleNav(rp('/'))}>
            <div className="sidebar-user-avatar">
              {user.profileImage ? (
                <img src={getFileUrl(user.profileImage)} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }} />
              ) : initials}
            </div>
            <div>
              <div className="sidebar-user-name">{user.name}</div>
              <span className="sidebar-user-role" style={{ background: roleColor + '22', color: roleColor }}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </span>
            </div>
          </div>
        )}

        <nav className="sidebar-nav">
          <div className="sidebar-nav-label">Main Menu</div>
          {mainMenu.map(item => <MenuItem key={item.path} item={item} />)}
          <div className="sidebar-divider" />
          <div className="sidebar-nav-label">More</div>
          {moreMenu.map(item => <MenuItem key={item.path} item={item} />)}
        </nav>

        <div className="sidebar-footer">
          <div className="sidebar-logout" onClick={handleLogout}>
            <div className="sidebar-logout-icon"><FaSignOutAlt /></div>
            <span className="sidebar-logout-label">Logout</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
